
const libPictView = require('pict-view');
const libPictSectionCode = require('pict-section-code');
const libManifestSchemaParts = require('../utils/manifest-schema-parts.js');

// Where a sample form built on the SampleCreation schema keeps the new sample's starting fields. Read by Fieldbook's
// New → Sample (lib/web/legacy/headlight_web_component_common/source/factories/report.factory.js createSampleForm).
const SAMPLE_CREATION_DEFAULT_ADDRESSES = { RecordAddress: 'SampleID__Record', LineItemsAddress: 'SampleLineItemIDs' };

class ReportNamedInstanceCreate extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		this._IsEditMode = false;
		this._CurrentRecordID = null;

		// Archetype dropdown options loaded from API
		this._ReportArchetypes = [];

		// Hash validation state
		this._HashDebounceTimer = null;
		this._HashValidationState = 'idle'; // idle | checking | valid | taken

		// Raw-JSON metadata editor (Fields | JSON tabs).  Editing the raw Options JSON is gated by the synthetic,
		// UX-only `ReportNamedInstance-EditJSON` entitlement (grantable in Platform Configuration → Entitlements);
		// without it the JSON tab is read-only.
		this._ActiveMetaTab = 'fields';   // 'fields' | 'json'
		this._CanEditJSON = false;
		this._JSONEditorView = null;
		this._JSONEditorHash = 'RNI-Metadata-JSONEditor';
		this._JSONSeed = '';

		// Where the loaded form's manifest lives, captured at LOAD time (manifest-schema-parts
		// getManifestStorage).  saveRecord() guards against the raw-JSON tab silently flipping a
		// schema-backed form to an embedded one; null in create mode, where there is nothing to detach.
		this._Storage = null;
	}

	/**
	 * Navigate to the create view for a brand-new record.
	 */
	onNavigateCreate()
	{
		this._IsEditMode = false;
		this._CurrentRecordID = null;
		this._HashValidationState = 'idle';
		this._ActiveMetaTab = 'fields';

		// Seed a blank record with the full Options structure that the platform
		// requires for dynamic forms to work.  These fields match the structure
		// produced by the headlight dynamic forms CLI push tool.
		this.AppData.CreateEditRecord = {
			Name: '',
			Hash: '',
			IDReportArchetype: 0,
			DefaultReport: 1,
			Options: {
				FormDefinition: {
					Product: 'HeadlightDynamicForm',
					DefaultFormManifest: {
						Scope: 'NewForm',
						Sections: [],
						Descriptors: {},
						ReferenceManifests: {},
						StaticOptionLists: [],
						PickLists: []
					}
				},
				Meta: {
					reportTitle: ''
				},
				FormOnly: true,
				TidingsData: {
					Type: 'headlight-dynamic-form',
					Renderer: 'html'
				},
				DefaultEmailFormat: 'pdf'
			}
		};

		this._loadArchetypesThenRender();
	}

	/**
	 * Navigate to the edit-metadata view for an existing record.
	 */
	onNavigateEdit(pIDReportNamedInstance)
	{
		this._IsEditMode = true;
		this._CurrentRecordID = pIDReportNamedInstance;
		this._HashValidationState = 'idle';
		this._ActiveMetaTab = 'fields';

		this.pict.HeadlightRestClient.getJSON(`ReportNamedInstance/${pIDReportNamedInstance}`, (pError, pBody) =>
		{
			if (pError)
			{
				this.pict.log.error(`Error loading ReportNamedInstance: ${pError}`);
				this.AppData.CreateEditRecord = null;
				return;
			}

			// Parse Options if it came back as a string
			let tmpOptions = pBody.Options;
			if (typeof tmpOptions === 'string')
			{
				try { tmpOptions = JSON.parse(tmpOptions); }
				catch (e) { tmpOptions = {}; }
			}
			if (!tmpOptions) { tmpOptions = {}; }

			this._Storage = libManifestSchemaParts.getManifestStorage(tmpOptions);

			this.AppData.CreateEditRecord = {
				IDReportNamedInstance: pBody.IDReportNamedInstance,
				GUIDReportNamedInstance: pBody.GUIDReportNamedInstance,
				Name: pBody.Name || '',
				Hash: pBody.Hash || '',
				IDReportArchetype: pBody.IDReportArchetype || 0,
				DefaultReport: pBody.DefaultReport != null ? pBody.DefaultReport : true,
				Options: tmpOptions,
				CreateDate: pBody.CreateDate,
				UpdateDate: pBody.UpdateDate,
				// Preserve the original hash so we can skip uniqueness checks
				// when the user hasn't changed it.
				_OriginalHash: pBody.Hash || '',
			};

			this._loadArchetypesThenRender();
		});
	}

	/**
	 * Load the list of ReportArchetypes, then render the form.
	 */
	_loadArchetypesThenRender()
	{
		this.pict.HeadlightRestClient.getJSON('ReportArchetypes/FilteredTo/FBV~IDReportArchetype~GT~0/0/250', (pError, pBody) =>
		{
			if (pError)
			{
				this.pict.log.warn('Could not load ReportArchetypes: ' + pError);
				this._ReportArchetypes = [];
			}
			else
			{
				this._ReportArchetypes = Array.isArray(pBody) ? pBody : [];
			}

			// Resolve the JSON-edit entitlement before painting so the JSON tab renders in the correct mode.
			this._resolveCanEditJSON(() =>
			{
				this.render('RNI-Create-Container', this.options.ContainerDestination || '#FormEditor-Application-Container');
				this._focusNameField();
			});
		});
	}

	/**
	 * Resolve whether the current user may hand-edit the raw Options JSON.  This is a UX-only gate backed by the
	 * synthetic `ReportNamedInstance-EditJSON` capability (granted via Platform Configuration → Entitlements).  We
	 * read the user's flat granted-permission list directly — the enforcement-config model isn't involved because
	 * this permission exists purely to unlock an editor affordance.  Super users (role tier 5) always qualify.
	 */
	_resolveCanEditJSON(fCallback)
	{
		const fDone = () => { if (typeof fCallback === 'function') { fCallback(); } };
		const tmpUser = this.AppData.CurrentUser || {};
		const tmpIDUser = tmpUser.UserID || tmpUser.IDUser || 0;
		const tmpIDCustomer = tmpUser.CustomerID || tmpUser.IDCustomer || 0;

		if (Number(tmpUser.UserRoleIndex) === 5)
		{
			this._CanEditJSON = true;
			return fDone();
		}
		if (!tmpIDUser || !tmpIDCustomer)
		{
			this._CanEditJSON = false;
			return fDone();
		}

		this.pict.HeadlightRestClient.getJSON(`Permissions/For/User/${tmpIDUser}/In/Customer/${tmpIDCustomer}`, (pError, pBody) =>
		{
			this._CanEditJSON = !pError && Array.isArray(pBody) && (pBody.indexOf('ReportNamedInstance-EditJSON') !== -1);
			fDone();
		});
	}

	/**
	 * Toggle between the guided Fields tab and the raw-JSON tab.  When switching to JSON, re-seed the editor from the
	 * current Options (after capturing any pending Fields edits) so the JSON always reflects the latest form state.
	 */
	switchMetaTab(pTab)
	{
		this._ActiveMetaTab = (pTab === 'json') ? 'json' : 'fields';
		const tmpIsJSON = (this._ActiveMetaTab === 'json');

		const tmpFieldsTab = document.getElementById('RNI-Meta-Tab-Fields');
		const tmpJSONTab = document.getElementById('RNI-Meta-Tab-JSON');
		const tmpFieldsPanel = document.getElementById('RNI-Meta-Panel-Fields');
		const tmpJSONPanel = document.getElementById('RNI-Meta-Panel-JSON');
		if (tmpFieldsTab) { tmpFieldsTab.className = tmpIsJSON ? '' : 'is-active'; }
		if (tmpJSONTab) { tmpJSONTab.className = tmpIsJSON ? 'is-active' : ''; }
		if (tmpFieldsPanel) { tmpFieldsPanel.classList.toggle('is-hidden', tmpIsJSON); }
		if (tmpJSONPanel) { tmpJSONPanel.classList.toggle('is-hidden', !tmpIsJSON); }

		if (tmpIsJSON)
		{
			const tmpRecord = this._readFormValues() || this.AppData.CreateEditRecord || {};
			const tmpOptions = (tmpRecord && tmpRecord.Options) ? tmpRecord.Options : {};
			let tmpSeed = '{}';
			try { tmpSeed = JSON.stringify(tmpOptions, null, 4); } catch (e) { tmpSeed = '{}'; }
			this._JSONSeed = tmpSeed;
			this._updateJSONNote();
			this._mountJSONEditor(tmpSeed);
		}
	}

	/**
	 * Show the JSON tab for an error WITHOUT throwing away what the user typed.
	 *
	 * switchMetaTab('json') reseeds the editor from the record and remounts it, which is right when
	 * arriving from the Fields tab and wrong when reporting a problem with text already in the editor
	 * — the user loses the edit they were told to fix, and cannot even see what was wrong.
	 */
	_revealJSONTabPreservingEdit()
	{
		if (this._ActiveMetaTab === 'json') { return; }
		this.switchMetaTab('json');
	}

	/** Show/hide the read-only banner on the JSON tab based on the resolved entitlement. */
	_updateJSONNote()
	{
		const tmpNote = document.getElementById('RNI-Create-JSON-Note');
		if (!tmpNote) { return; }
		if (this._CanEditJSON)
		{
			tmpNote.classList.add('is-hidden');
			tmpNote.innerHTML = '';
		}
		else
		{
			tmpNote.classList.remove('is-hidden');
			tmpNote.innerHTML = '<span class="icon"><i class="fas fa-lock"></i></span> <span>Read-only. Editing the raw JSON requires the <strong>Edit report JSON</strong> entitlement &mdash; an administrator can grant it in Platform Configuration &rarr; Entitlements.</span>';
		}
	}

	/** Lazily register the pict-section-code view used for the raw Options JSON (mirrors the FormClone editor). */
	_ensureJSONEditorView()
	{
		if (this._JSONEditorView) { return this._JSONEditorView; }
		const tmpHash = this._JSONEditorHash;
		this._JSONEditorView = this.pict.addView(
			tmpHash,
			{
				ViewIdentifier: tmpHash,
				Language: 'json',
				ReadOnly: false,
				LineNumbers: true,
				DefaultCode: '',
				AddClosing: false,
				IndentOn: false,
				MoveToNewLine: false,
				AutoRender: false,
				RenderOnLoad: false,
				DefaultRenderable: 'RNI-JSONEditor-Wrap',
				DefaultDestinationAddress: '#RNI-Create-JSON-Container',
				TargetElementAddress: '#RNI-Create-JSON-Container',
				Renderables:
				[
					{
						RenderableHash: 'RNI-JSONEditor-Wrap',
						TemplateHash: 'RNI-JSONEditor-Container',
						DestinationAddress: '#RNI-Create-JSON-Container'
					}
				]
			},
			libPictSectionCode
		);
		this._JSONEditorView.initialize();
		return this._JSONEditorView;
	}

	/** (Re)mount the JSON editor into its container and seed it, honouring the read-only gate. */
	_mountJSONEditor(pSeed)
	{
		const tmpContainerList = this.pict.ContentAssignment.getElement('#RNI-Create-JSON-Container');
		if (!tmpContainerList || !tmpContainerList[0]) { return; }
		const tmpEditor = this._ensureJSONEditorView();
		if (tmpEditor.codeJar && typeof tmpEditor.destroy === 'function')
		{
			tmpEditor.destroy();
			tmpEditor.initialRenderComplete = false;
		}
		tmpEditor.render();
		if (tmpEditor.codeJar)
		{
			tmpEditor.setCode(pSeed || '');
			if (typeof tmpEditor.setReadOnly === 'function') { tmpEditor.setReadOnly(!this._CanEditJSON); }
		}
	}

	_focusNameField()
	{
		setTimeout(() =>
		{
			const tmpNameInput = document.getElementById('RNI-Create-Name');
			if (tmpNameInput)
			{
				tmpNameInput.focus();
			}
		}, 50);
	}

	/**
	 * Read form field values from the DOM back into AppData.CreateEditRecord.
	 */
	_readFormValues()
	{
		const tmpRecord = this.AppData.CreateEditRecord;
		if (!tmpRecord) { return null; }

		const tmpName = document.getElementById('RNI-Create-Name');
		const tmpHash = document.getElementById('RNI-Create-Hash');
		const tmpIDReportArchetype = document.getElementById('RNI-Create-IDReportArchetype');
		const tmpDefaultReport = document.getElementById('RNI-Create-DefaultReport');
		const tmpFormOnly = document.getElementById('RNI-Create-FormOnly');
		const tmpTidingsType = document.getElementById('RNI-Create-TidingsType');
		const tmpTidingsRenderer = document.getElementById('RNI-Create-TidingsRenderer');
		const tmpDefaultEmailFormat = document.getElementById('RNI-Create-DefaultEmailFormat');
		const tmpReportTitle = document.getElementById('RNI-Create-ReportTitle');

		if (tmpName) { tmpRecord.Name = tmpName.value.trim(); }
		if (tmpHash) { tmpRecord.Hash = tmpHash.value.trim(); }
		if (tmpIDReportArchetype) { tmpRecord.IDReportArchetype = parseInt(tmpIDReportArchetype.value, 10) || 0; }
		if (tmpDefaultReport) { tmpRecord.DefaultReport = tmpDefaultReport.checked; }

		// Ensure Options structure exists
		if (!tmpRecord.Options) { tmpRecord.Options = {}; }
		if (!tmpRecord.Options.TidingsData) { tmpRecord.Options.TidingsData = {}; }
		if (!tmpRecord.Options.Meta) { tmpRecord.Options.Meta = {}; }

		if (tmpFormOnly) { tmpRecord.Options.FormOnly = tmpFormOnly.checked; }
		const tmpStartsSample = document.getElementById('RNI-Create-StartsSample');
		if (tmpStartsSample)
		{
			if (!tmpStartsSample.checked)
			{
				delete tmpRecord.Options.SampleCreation;
			}
			else if (!tmpRecord.Options.SampleCreation || (typeof tmpRecord.Options.SampleCreation !== 'object'))
			{
				tmpRecord.Options.SampleCreation = Object.assign({}, SAMPLE_CREATION_DEFAULT_ADDRESSES);
			}
		}
		if (tmpTidingsType) { tmpRecord.Options.TidingsData.Type = tmpTidingsType.value.trim(); }
		if (tmpTidingsRenderer) { tmpRecord.Options.TidingsData.Renderer = tmpTidingsRenderer.value.trim(); }
		if (tmpDefaultEmailFormat) { tmpRecord.Options.DefaultEmailFormat = tmpDefaultEmailFormat.value.trim(); }
		if (tmpReportTitle) { tmpRecord.Options.Meta.reportTitle = tmpReportTitle.value.trim(); }

		return tmpRecord;
	}

	// ---- Hash uniqueness validation ----

	/**
	 * Called on every keystroke in the Hash input.  Debounces 500ms then
	 * queries the server to check whether the hash is already taken.
	 */
	onHashInput()
	{
		if (this._HashDebounceTimer)
		{
			clearTimeout(this._HashDebounceTimer);
		}

		const tmpHashInput = document.getElementById('RNI-Create-Hash');
		if (!tmpHashInput) { return; }

		const tmpHash = tmpHashInput.value.trim();
		if (!tmpHash)
		{
			this._setHashValidationState('idle');
			return;
		}

		// In edit mode, if the hash hasn't changed from the original, skip the check
		if (this._IsEditMode && this.AppData.CreateEditRecord && tmpHash === this.AppData.CreateEditRecord._OriginalHash)
		{
			this._setHashValidationState('idle');
			return;
		}

		this._setHashValidationState('checking');

		this._HashDebounceTimer = setTimeout(() =>
		{
			this._checkHashUniqueness(tmpHash);
		}, 500);
	}

	_checkHashUniqueness(pHash)
	{
		const tmpEncodedHash = encodeURIComponent(pHash);
		this.pict.HeadlightRestClient.getJSON(
			`ReportNamedInstances/FilteredTo/FBV~Hash~EQ~${tmpEncodedHash}/0/1`,
			(pError, pBody) =>
			{
				// If the input changed while we were waiting, ignore this response
				const tmpHashInput = document.getElementById('RNI-Create-Hash');
				if (!tmpHashInput || tmpHashInput.value.trim() !== pHash)
				{
					return;
				}

				if (pError)
				{
					this._setHashValidationState('idle');
					return;
				}

				const tmpResults = Array.isArray(pBody) ? pBody : [];

				if (tmpResults.length === 0)
				{
					this._setHashValidationState('valid');
				}
				else
				{
					// If we're editing and the match is ourselves, that's fine
					if (this._IsEditMode && this.AppData.CreateEditRecord &&
						tmpResults[0].IDReportNamedInstance === this.AppData.CreateEditRecord.IDReportNamedInstance)
					{
						this._setHashValidationState('valid');
					}
					else
					{
						this._setHashValidationState('taken');
					}
				}
			}
		);
	}

	_setHashValidationState(pState)
	{
		this._HashValidationState = pState;

		const tmpHashInput = document.getElementById('RNI-Create-Hash');
		const tmpHashHelp = document.getElementById('RNI-Create-Hash-Help');
		const tmpHashIcon = document.getElementById('RNI-Create-Hash-Status-Icon');

		if (!tmpHashInput || !tmpHashHelp) { return; }

		// Reset classes
		tmpHashInput.classList.remove('is-success', 'is-danger', 'is-warning');

		switch (pState)
		{
		case 'checking':
			tmpHashInput.classList.add('is-warning');
			tmpHashHelp.className = 'help is-warning';
			tmpHashHelp.textContent = 'Checking availability...';
			if (tmpHashIcon) { tmpHashIcon.innerHTML = '<i class="fas fa-spinner fa-spin"></i>'; }
			break;
		case 'valid':
			tmpHashInput.classList.add('is-success');
			tmpHashHelp.className = 'help is-success';
			tmpHashHelp.textContent = 'This hash is available.';
			if (tmpHashIcon) { tmpHashIcon.innerHTML = '<i class="fas fa-check"></i>'; }
			break;
		case 'taken':
			tmpHashInput.classList.add('is-danger');
			tmpHashHelp.className = 'help is-danger';
			tmpHashHelp.textContent = 'This hash is already in use. Please choose another.';
			if (tmpHashIcon) { tmpHashIcon.innerHTML = '<i class="fas fa-times"></i>'; }
			break;
		default: // idle
			tmpHashHelp.className = 'help';
			tmpHashHelp.textContent = 'A unique identifier. If left blank, the Name will be used.';
			if (tmpHashIcon) { tmpHashIcon.innerHTML = '<i class="fas fa-hashtag"></i>'; }
			break;
		}
	}

	// ---- Save ----

	/**
	 * Validate and save the record (create or update).
	 */
	saveRecord()
	{
		const tmpRecord = this._readFormValues();
		if (!tmpRecord)
		{
			return;
		}

		// Validate required fields
		if (!tmpRecord.Name)
		{
			this._showNotification('Name is required.', 'warning');
			const tmpNameInput = document.getElementById('RNI-Create-Name');
			if (tmpNameInput) { tmpNameInput.focus(); }
			return;
		}

		// If Hash is empty, default to Name
		if (!tmpRecord.Hash)
		{
			tmpRecord.Hash = tmpRecord.Name;
		}

		// Block save if we know the hash is taken
		if (this._HashValidationState === 'taken')
		{
			this._showNotification('The Hash is already in use. Please choose a unique Hash.', 'danger');
			const tmpHashInput = document.getElementById('RNI-Create-Hash');
			if (tmpHashInput) { tmpHashInput.focus(); }
			return;
		}

		// If the user edited the raw Options JSON (and is entitled), it is authoritative for Options — parse and
		// validate it, replacing the field-derived Options.  An untouched or read-only editor is ignored.
		if (this._CanEditJSON && this._JSONEditorView && this._JSONEditorView.codeJar && (typeof this._JSONEditorView.getCode === 'function'))
		{
			const tmpJSONText = this._JSONEditorView.getCode();
			if ((typeof tmpJSONText === 'string') && (tmpJSONText !== this._JSONSeed))
			{
				let tmpParsed = null;
				try { tmpParsed = JSON.parse(tmpJSONText); }
				catch (e)
				{
					this._showNotification('The Options JSON is not valid JSON — fix it before saving.', 'danger');
					this._revealJSONTabPreservingEdit();
					return;
				}
				if (tmpParsed && (typeof tmpParsed === 'object') && !Array.isArray(tmpParsed))
				{
					// A pasted FormDefinition outranks the schema links at render time, so saving this would
					// quietly detach a schema-backed form — the record would keep FormManifestSchemaID while
					// the runtime ignored it.  Compare against the mode captured at LOAD, not the pasted JSON.
					// Any FormDefinition object counts, even {} or null: same `typeof === 'object'` test as the runtime
					// loaders (ManifestLoaderService._resolveManifest, report_definitions LoadFormManifest).
					if (this._Storage && (this._Storage.Mode === 'schema'))
					{
						const tmpPastedStorage = libManifestSchemaParts.getManifestStorage(tmpParsed);
						// Every loader merges the footer only into the header's manifest (report_definitions LoadFormManifest,
						// ManifestLoaderService._resolveManifest, the Materials / Payment app loaders), so a kept footer link loads nothing.
						const tmpHeaderDropped = Boolean(this._Storage.Header) && !tmpPastedStorage.Header;
						if ((typeof tmpParsed.FormDefinition === 'object') || (tmpPastedStorage.Mode !== 'schema') || tmpHeaderDropped)
						{
							this._showNotification(this._schemaDetachMessage(tmpParsed, tmpPastedStorage), 'danger');
							this._revealJSONTabPreservingEdit();
							return;
						}
					}
					tmpRecord.Options = tmpParsed;
				}
				else
				{
					this._showNotification('The Options JSON must be a JSON object.', 'danger');
					this._revealJSONTabPreservingEdit();
					return;
				}
			}
		}

		// Get IDCustomer from session
		const tmpIDCustomer = (this.AppData.CurrentUser && this.AppData.CurrentUser.CustomerID)
			? parseInt(this.AppData.CurrentUser.CustomerID, 10)
			: 0;

		// Build the payload
		const tmpPayload = {
			Name: tmpRecord.Name,
			Hash: tmpRecord.Hash,
			IDCustomer: tmpIDCustomer,
			IDReportArchetype: tmpRecord.IDReportArchetype,
			DefaultReport: tmpRecord.DefaultReport ? 1 : 0,
		};

		// If editing, include identity fields
		if (this._IsEditMode && tmpRecord.IDReportNamedInstance)
		{
			tmpPayload.IDReportNamedInstance = tmpRecord.IDReportNamedInstance;
			tmpPayload.GUIDReportNamedInstance = tmpRecord.GUIDReportNamedInstance;
		}

		// Send Options as an OBJECT (not a JSON string).  The API's
		// serializeOptions hook expects an object so it can diff it against
		// the archetype before persisting.  Sending a string causes the diff
		// to produce garbage, breaking TidingsData and other required fields.
		if (tmpRecord.Options)
		{
			// Ensure the critical FormDefinition.Product field is always present — except on schema-backed forms,
			// where any FormDefinition makes the runtime load it instead of the linked ManifestSchema.
			if (libManifestSchemaParts.getManifestStorage(tmpRecord.Options).Mode !== 'schema')
			{
				if (!tmpRecord.Options.FormDefinition) { tmpRecord.Options.FormDefinition = {}; }
				if (!tmpRecord.Options.FormDefinition.Product)
				{
					tmpRecord.Options.FormDefinition.Product = 'HeadlightDynamicForm';
				}
			}

			tmpPayload.Options = tmpRecord.Options;
		}
		else
		{
			// Fallback: seed with the full default structure
			tmpPayload.Options = {
				FormDefinition: {
					Product: 'HeadlightDynamicForm',
					DefaultFormManifest: {
						Scope: 'NewForm',
						Sections: [],
						Descriptors: {},
						ReferenceManifests: {},
						StaticOptionLists: [],
						PickLists: []
					}
				},
				Meta: { reportTitle: '' },
				FormOnly: true,
				TidingsData: {
					Type: 'headlight-dynamic-form',
					Renderer: 'html'
				},
				DefaultEmailFormat: 'pdf'
			};
		}

		// Show saving state
		const tmpSaveButton = document.getElementById('RNI-Create-Save-Button');
		if (tmpSaveButton)
		{
			tmpSaveButton.classList.add('is-loading');
		}

		this.pict.HeadlightRestClient.putJSON('ReportNamedInstance/Upsert', tmpPayload, (pError, pBody) =>
		{
			if (tmpSaveButton)
			{
				tmpSaveButton.classList.remove('is-loading');
			}

			if (pError)
			{
				this.pict.log.error(`Error saving ReportNamedInstance: ${pError}`);
				this._showNotification('Error saving record. Please try again.', 'danger');
				return;
			}

			this._showNotification('Record saved successfully!', 'success');

			if (pBody && pBody.IDReportNamedInstance)
			{
				// Default: navigate to the form editor for the new/updated record. A host that only reuses the
				// metadata editor (e.g. the config app's Document Types) overrides AfterSaveRoute with its own
				// destination; `{IDReportNamedInstance}` / `{GUIDReportNamedInstance}` are substituted from the saved record.
				const tmpRoute = (this.options.AfterSaveRoute || '#/ReportNamedInstance/{IDReportNamedInstance}/Edit')
					.replace('{IDReportNamedInstance}', pBody.IDReportNamedInstance)
					.replace('{GUIDReportNamedInstance}', pBody.GUIDReportNamedInstance || '');
				window.location.hash = tmpRoute;
			}
		});
	}

	/**
	 * Why saveRecord() refused a raw-JSON paste on a schema-backed form, naming the schema link fields the paste removed.
	 *
	 * @param {object} pParsed - The pasted Options.
	 * @param {object} pPastedStorage - getManifestStorage() of the pasted Options.
	 * @return {string}
	 */
	_schemaDetachMessage(pParsed, pPastedStorage)
	{
		const tmpLoadedHeader = this._Storage.Header;
		const tmpLoadedFooter = this._Storage.Footer;
		const tmpHeaderRemoved = Boolean(tmpLoadedHeader) && !pPastedStorage.Header;
		const tmpFooterRemoved = Boolean(tmpLoadedFooter) && !pPastedStorage.Footer;
		if (!tmpHeaderRemoved && (pPastedStorage.Header || pPastedStorage.Footer))
		{
			return 'This form is schema-backed, and the pasted Options carry their own FormDefinition — saving it would detach the form from its linked ManifestSchema, which the runtime would then ignore. Remove the FormDefinition, or edit the manifest in the Form Editor instead.';
		}

		const tmpRemovedFields = [];
		if (tmpHeaderRemoved && (tmpLoadedHeader.ID > 0))
		{
			tmpRemovedFields.push('FormManifestSchemaID');
		}
		if (tmpHeaderRemoved && tmpLoadedHeader.Hash)
		{
			tmpRemovedFields.push('FormManifestSchemaHash');
		}
		if (tmpFooterRemoved && (tmpLoadedFooter.ID > 0))
		{
			tmpRemovedFields.push('FormFooterManifestSchemaID');
		}
		if (tmpFooterRemoved && tmpLoadedFooter.Hash)
		{
			tmpRemovedFields.push('FormFooterManifestSchemaHash');
		}
		const tmpRemovedLink = `remove its link to the ManifestSchema (${tmpRemovedFields.join(', ')})`;

		if (typeof pParsed.FormDefinition === 'object')
		{
			return `This form is schema-backed, and the pasted Options ${tmpRemovedLink} and carry their own FormDefinition — saving it would detach the form from its ManifestSchema. Put the link back and remove the FormDefinition, or edit the manifest in the Form Editor instead.`;
		}
		const tmpConsequence = pPastedStorage.Footer
			? 'the footer link they keep loads nothing on its own, so saving it would leave the form without its manifest'
			: 'saving it would leave the form without its manifest';
		return `This form is schema-backed, and the pasted Options ${tmpRemovedLink} — ${tmpConsequence}. Put the link back to save your other changes.`;
	}

	// ---- Render lifecycle ----

	onAfterRender()
	{
		super.onAfterRender();

		const tmpRecord = this.AppData.CreateEditRecord;

		// Set the page title based on create vs edit mode
		const tmpTitleEl = document.getElementById('RNI-Create-Page-Title');
		if (tmpTitleEl)
		{
			tmpTitleEl.textContent = this._IsEditMode ? 'Edit Record Metadata' : 'Create New Record';
		}

		const tmpSaveLabelEl = document.getElementById('RNI-Create-Save-Label');
		if (tmpSaveLabelEl)
		{
			tmpSaveLabelEl.textContent = this._IsEditMode ? 'Save Changes' : 'Create Record';
		}

		// Show or hide the delete button (only visible in edit mode)
		const tmpDeleteBtn = document.getElementById('RNI-Create-Delete-Button');
		if (tmpDeleteBtn)
		{
			if (this._IsEditMode)
			{
				tmpDeleteBtn.classList.remove('is-hidden');
			}
			else
			{
				tmpDeleteBtn.classList.add('is-hidden');
			}
		}

		// Set checkbox state
		const tmpDefaultReport = document.getElementById('RNI-Create-DefaultReport');
		if (tmpDefaultReport && tmpRecord)
		{
			tmpDefaultReport.checked = !!tmpRecord.DefaultReport;
		}

		// Set FormOnly checkbox
		const tmpFormOnly = document.getElementById('RNI-Create-FormOnly');
		if (tmpFormOnly && tmpRecord)
		{
			tmpFormOnly.checked = tmpRecord.Options && tmpRecord.Options.FormOnly !== undefined
				? !!tmpRecord.Options.FormOnly
				: true;
		}

		// Offered for forms built on the SampleCreation schema, and for any form that already starts samples.
		const tmpStartsSample = document.getElementById('RNI-Create-StartsSample');
		const tmpStartsSampleField = document.getElementById('RNI-Create-StartsSample-Field');
		if (tmpStartsSample && tmpStartsSampleField)
		{
			const tmpOptions = (tmpRecord && tmpRecord.Options) || {};
			tmpStartsSample.checked = !!tmpOptions.SampleCreation;
			tmpStartsSampleField.classList.toggle('is-hidden', !(tmpStartsSample.checked || (tmpOptions.FormManifestSchemaHash === 'SampleCreation')));
		}

		// Set TidingsData fields
		const tmpTidingsType = document.getElementById('RNI-Create-TidingsType');
		if (tmpTidingsType && tmpRecord && tmpRecord.Options && tmpRecord.Options.TidingsData)
		{
			tmpTidingsType.value = tmpRecord.Options.TidingsData.Type || 'headlight-dynamic-form';
		}
		const tmpTidingsRenderer = document.getElementById('RNI-Create-TidingsRenderer');
		if (tmpTidingsRenderer && tmpRecord && tmpRecord.Options && tmpRecord.Options.TidingsData)
		{
			tmpTidingsRenderer.value = tmpRecord.Options.TidingsData.Renderer || 'html';
		}

		// Set DefaultEmailFormat
		const tmpDefaultEmailFormat = document.getElementById('RNI-Create-DefaultEmailFormat');
		if (tmpDefaultEmailFormat && tmpRecord && tmpRecord.Options)
		{
			tmpDefaultEmailFormat.value = tmpRecord.Options.DefaultEmailFormat || 'pdf';
		}

		// Set Report Title
		const tmpReportTitle = document.getElementById('RNI-Create-ReportTitle');
		if (tmpReportTitle && tmpRecord && tmpRecord.Options && tmpRecord.Options.Meta)
		{
			tmpReportTitle.value = tmpRecord.Options.Meta.reportTitle || '';
		}

		// Populate the archetype dropdown
		const tmpArchetypeSelect = document.getElementById('RNI-Create-IDReportArchetype');
		if (tmpArchetypeSelect)
		{
			let tmpOptionsHTML = '<option value="0">-- No Archetype --</option>';
			for (let i = 0; i < this._ReportArchetypes.length; i++)
			{
				const tmpArch = this._ReportArchetypes[i];
				const tmpSelected = (tmpRecord && tmpRecord.IDReportArchetype === tmpArch.IDReportArchetype) ? ' selected' : '';
				// Use Description as the primary human-readable label, with Archetype as secondary context
				let tmpLabel = '';
				if (tmpArch.Description && tmpArch.Archetype)
				{
					tmpLabel = `${tmpArch.Description} (${tmpArch.Archetype})`;
				}
				else if (tmpArch.Description)
				{
					tmpLabel = tmpArch.Description;
				}
				else if (tmpArch.Archetype)
				{
					tmpLabel = tmpArch.Archetype;
				}
				else
				{
					tmpLabel = `Archetype ${tmpArch.IDReportArchetype}`;
				}
				tmpOptionsHTML += `<option value="${tmpArch.IDReportArchetype}"${tmpSelected}>${tmpLabel}</option>`;
			}
			tmpArchetypeSelect.innerHTML = tmpOptionsHTML;
		}

		// Wire up the hash input for debounced uniqueness checking
		const tmpHashInput = document.getElementById('RNI-Create-Hash');
		if (tmpHashInput)
		{
			tmpHashInput.addEventListener('input', () => { this.onHashInput(); });
		}
	}

	// ---- Delete ----

	/**
	 * Show the delete confirmation modal (edit mode only).
	 */
	confirmDelete()
	{
		if (!this._IsEditMode || !this._CurrentRecordID) { return; }
		const tmpModal = document.getElementById('RNI-Delete-Confirm-Modal');
		if (tmpModal)
		{
			tmpModal.classList.add('is-active');
		}
	}

	cancelDelete()
	{
		const tmpModal = document.getElementById('RNI-Delete-Confirm-Modal');
		if (tmpModal)
		{
			tmpModal.classList.remove('is-active');
		}
	}

	executeDelete()
	{
		if (!this._IsEditMode || !this._CurrentRecordID) { return; }

		const tmpDeleteBtn = document.getElementById('RNI-Delete-Confirm-Button');
		if (tmpDeleteBtn) { tmpDeleteBtn.classList.add('is-loading'); }

		this.pict.HeadlightRestClient.deleteEntity('ReportNamedInstance', this._CurrentRecordID, (pError) =>
		{
			if (tmpDeleteBtn) { tmpDeleteBtn.classList.remove('is-loading'); }

			this.cancelDelete();

			if (pError)
			{
				this.pict.log.error(`Error deleting ReportNamedInstance: ${pError}`);
				this._showNotification('Error deleting record. Please try again.', 'danger');
				return;
			}

			this._showNotification('Record deleted successfully.', 'success');
			// Navigate back to the list after a brief pause
			setTimeout(() =>
			{
				window.location.hash = this.options.ListRoute || '#/ReportNamedInstances';
			}, 800);
		});
	}

	// ---- Helpers ----

	_showNotification(pMessage, pType)
	{
		const tmpContainer = document.getElementById('RNI-Create-Notification-Area');
		if (tmpContainer)
		{
			tmpContainer.innerHTML = /*html*/`
				<div class="notification is-${pType} is-soft">
					<button class="delete" onclick="this.parentElement.remove()"></button>
					${pMessage}
				</div>`;
			setTimeout(() =>
			{
				tmpContainer.innerHTML = '';
			}, 4000);
		}
	}

	navigateToList()
	{
		window.location.hash = this.options.ListRoute || '#/ReportNamedInstances';
	}

	/**
	 * Custom-view entry point for pict-section-recordset's `RecordSetCustomViews` override (see
	 * RecordSet-RecordBaseView.delegateToCustomView). A host record set can point its View/Edit/Create
	 * screens at this view; PSRS calls this with `{ Action, RecordSet, GUIDRecord }`. Create opens a blank
	 * editor; View/Edit resolve the route's GUID to the record id, then open it for editing.
	 * @param {{Action:string, RecordSet:string, GUIDRecord:string}} pContext
	 */
	onRecordSetCustomView(pContext)
	{
		if (!pContext || pContext.Action === 'Create' || !pContext.GUIDRecord)
		{
			return this.onNavigateCreate();
		}
		this.pict.HeadlightRestClient.getJSON(`ReportNamedInstances/FilteredTo/FBV~GUIDReportNamedInstance~EQ~${encodeURIComponent(pContext.GUIDRecord)}/0/1`, (pError, pBody) =>
		{
			const tmpRecord = (!pError && Array.isArray(pBody) && pBody[0]) ? pBody[0] : null;
			if (tmpRecord && tmpRecord.IDReportNamedInstance)
			{
				this.onNavigateEdit(tmpRecord.IDReportNamedInstance);
			}
			else
			{
				this.pict.log.warn(`ReportNamedInstance custom view could not resolve a record for GUID [${pContext.GUIDRecord}].`);
			}
		});
	}
}

module.exports = ReportNamedInstanceCreate;

module.exports.default_configuration = (
{
	ViewIdentifier: 'ReportNamedInstanceCreate',

	DefaultRenderable: 'RNI-Create-Container',
	DefaultDestinationAddress: '#FormEditor-Application-Container',

	AutoRender: false,

	CSS: /*css*/`
.rni-create-container
{
	display: flex;
	flex-direction: column;
	height: calc(100vh - 80px);
}
.rni-create-toolbar
{
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 16px;
	background: var(--bulma-scheme-main-bis);
	border-bottom: 1px solid var(--bulma-border);
	flex-wrap: wrap;
	gap: 8px;
}
.rni-create-toolbar-left
{
	display: flex;
	align-items: center;
	gap: 12px;
}
.rni-create-toolbar-right
{
	display: flex;
	align-items: center;
	gap: 8px;
}
.rni-create-title
{
	font-size: 1.1rem;
	font-weight: 600;
	margin: 0;
}
.rni-create-body
{
	flex: 1;
	overflow: auto;
	padding: 24px;
}
.rni-create-form
{
	max-width: 720px;
	margin: 0 auto;
}
.rni-create-form .field
{
	margin-bottom: 1rem;
}
.rni-create-meta
{
	margin-top: 1.5rem;
	padding-top: 1rem;
	border-top: 1px solid var(--bulma-border);
}
.rni-create-meta .field
{
	margin-bottom: 0.75rem;
}
.rni-meta-tabs
{
	margin-bottom: 0.75rem;
}
.rni-json-wrap
{
	max-width: 1100px;
	margin: 0 auto;
}
.rni-json-note .icon
{
	vertical-align: middle;
}
.rni-json-help
{
	margin-bottom: 0.5rem;
}
.rni-json-editor
{
	min-height: 440px;
}
.rni-json-editor .pict-code-editor-wrap
{
	min-height: 440px;
	max-height: 70vh;
}
`,

	Templates:
	[
		{
			Hash: 'RNI-Create-Container-Template',
			Template: /*html*/`
<div class="rni-create-container">
	<div id="RNI-Create-Notification-Area"></div>
	<div class="rni-create-toolbar">
		<div class="rni-create-toolbar-left">
			<button class="button is-small" onclick="_Pict.views.ReportNamedInstanceCreate.navigateToList()">
				<span class="icon"><i class="fas fa-arrow-left"></i></span>
				<span>Back to List</span>
			</button>
			<p class="rni-create-title">
				<span id="RNI-Create-Page-Title"></span>
			</p>
		</div>
		<div class="rni-create-toolbar-right">
			<button id="RNI-Create-Delete-Button" class="button is-small is-danger is-hidden" onclick="_Pict.views.ReportNamedInstanceCreate.confirmDelete()">
				<span class="icon"><i class="fas fa-trash"></i></span>
				<span>Delete</span>
			</button>
			<button id="RNI-Create-Save-Button" class="button is-small is-success" onclick="_Pict.views.ReportNamedInstanceCreate.saveRecord()">
				<span class="icon"><i class="fas fa-floppy-disk"></i></span>
				<span><span id="RNI-Create-Save-Label"></span></span>
			</button>
		</div>
	</div>
	<div class="rni-create-body">
		<div class="tabs is-small rni-meta-tabs">
			<ul>
				<li id="RNI-Meta-Tab-Fields" class="is-active"><a onclick="_Pict.views.ReportNamedInstanceCreate.switchMetaTab('fields')"><span class="icon is-small"><i class="fas fa-list-ul"></i></span><span>Fields</span></a></li>
				<li id="RNI-Meta-Tab-JSON"><a onclick="_Pict.views.ReportNamedInstanceCreate.switchMetaTab('json')"><span class="icon is-small"><i class="fas fa-code"></i></span><span>JSON</span></a></li>
			</ul>
		</div>
		<div id="RNI-Meta-Panel-Fields">
		<div class="rni-create-form">
			<h3 class="title is-5">
				<span class="icon"><i class="fas fa-file-circle-plus"></i></span>
				Report Named Instance
			</h3>

			<div class="field">
				<label class="label">Name <span class="has-text-danger">*</span></label>
				<div class="control has-icons-left">
					<input id="RNI-Create-Name" class="input" type="text"
						placeholder="Enter a descriptive name..."
						value="{~D:AppData.CreateEditRecord.Name~}" />
					<span class="icon is-left"><i class="fas fa-font"></i></span>
				</div>
				<p class="help">The display name for this report instance.</p>
			</div>

			<div class="field">
				<label class="label">Hash</label>
				<div class="control has-icons-left has-icons-right">
					<input id="RNI-Create-Hash" class="input" type="text"
						placeholder="Defaults to Name if left empty"
						value="{~D:AppData.CreateEditRecord.Hash~}" />
					<span class="icon is-left"><i class="fas fa-hashtag"></i></span>
					<span id="RNI-Create-Hash-Status-Icon" class="icon is-right"><i class="fas fa-hashtag"></i></span>
				</div>
				<p id="RNI-Create-Hash-Help" class="help">A unique identifier. If left blank, the Name will be used.</p>
			</div>

			<div class="rni-create-meta">
				<h5 class="title is-6 mb-3">
					<span class="icon"><i class="fas fa-sliders"></i></span>
					Additional Settings
				</h5>

				<div class="field">
					<label class="label">Report Archetype</label>
					<div class="control has-icons-left">
						<div class="select is-fullwidth">
							<select id="RNI-Create-IDReportArchetype">
								<option value="0">Loading...</option>
							</select>
						</div>
						<span class="icon is-left"><i class="fas fa-layer-group"></i></span>
					</div>
					<p class="help">The report archetype for this instance.</p>
				</div>

				<div class="field">
					<label class="checkbox">
						<input id="RNI-Create-DefaultReport" type="checkbox" checked />
						Default Report
					</label>
					<p class="help">Whether this report is available by default (without a project join).</p>
				</div>

				<div class="field">
					<label class="checkbox">
						<input id="RNI-Create-FormOnly" type="checkbox" />
						Form Only
					</label>
					<p class="help">Marks this as a dynamic form (not a traditional report).</p>
				</div>

				<div id="RNI-Create-StartsSample-Field" class="field is-hidden">
					<label class="checkbox">
						<input id="RNI-Create-StartsSample" type="checkbox" />
						Starts a new sample
					</label>
					<p class="help">Fieldbook’s New → Sample, and the Sample Plan dashboard’s New Sample buttons, create this form on the project.</p>
				</div>
			</div>

			<div class="rni-create-meta">
				<h5 class="title is-6 mb-3">
					<span class="icon"><i class="fas fa-gears"></i></span>
					Platform Configuration
				</h5>

				<div class="field">
					<label class="label">Report Title</label>
					<div class="control has-icons-left">
						<input id="RNI-Create-ReportTitle" class="input" type="text"
							placeholder="Optional display title for reports"
							value="" />
						<span class="icon is-left"><i class="fas fa-heading"></i></span>
					</div>
					<p class="help">Title shown when this form is rendered as a report.</p>
				</div>

				<div class="columns">
					<div class="column">
						<div class="field">
							<label class="label">Tidings Type</label>
							<div class="control has-icons-left">
								<input id="RNI-Create-TidingsType" class="input" type="text"
									placeholder="headlight-dynamic-form"
									value="headlight-dynamic-form" />
								<span class="icon is-left"><i class="fas fa-tag"></i></span>
							</div>
							<p class="help">The document type identifier used by the report service.</p>
						</div>
					</div>
					<div class="column">
						<div class="field">
							<label class="label">Renderer</label>
							<div class="control has-icons-left">
								<div class="select is-fullwidth">
									<select id="RNI-Create-TidingsRenderer">
										<option value="html">html</option>
										<option value="pdf">pdf</option>
										<option value="json">json</option>
										<option value="default">default</option>
									</select>
								</div>
								<span class="icon is-left"><i class="fas fa-display"></i></span>
							</div>
							<p class="help">How the report service renders this form.</p>
						</div>
					</div>
				</div>

				<div class="field">
					<label class="label">Default Email Format</label>
					<div class="control has-icons-left">
						<div class="select is-fullwidth">
							<select id="RNI-Create-DefaultEmailFormat">
								<option value="pdf">pdf</option>
								<option value="html">html</option>
							</select>
						</div>
						<span class="icon is-left"><i class="fas fa-envelope"></i></span>
					</div>
					<p class="help">The format used when emailing this form.</p>
				</div>
			</div>
		</div>
		</div>
		<div id="RNI-Meta-Panel-JSON" class="is-hidden">
			<div class="rni-json-wrap">
				<div id="RNI-Create-JSON-Note" class="notification is-warning is-soft rni-json-note is-hidden"></div>
				<p class="help rni-json-help">The full <strong>Options</strong> configuration for this report, as raw JSON. Editing here overrides the Fields tab for the Options object when you Save.</p>
				<div id="RNI-Create-JSON-Container" class="rni-json-editor"></div>
			</div>
		</div>
	</div>
	<div id="RNI-Delete-Confirm-Modal" class="modal">
		<div class="modal-background" onclick="_Pict.views.ReportNamedInstanceCreate.cancelDelete()"></div>
		<div class="modal-card">
			<header class="modal-card-head">
				<p class="modal-card-title has-text-danger">
					<span class="icon"><i class="fas fa-exclamation-triangle"></i></span>
					<span>Confirm Delete</span>
				</p>
				<button class="delete" aria-label="close" onclick="_Pict.views.ReportNamedInstanceCreate.cancelDelete()"></button>
			</header>
			<section class="modal-card-body">
				<p>Are you sure you want to delete <strong>{~D:AppData.CreateEditRecord.Name~}</strong>?</p>
				<p class="mt-2 has-text-grey">This action cannot be undone. The record and its form definition will be permanently removed.</p>
			</section>
			<footer class="modal-card-foot">
				<div class="buttons">
					<button id="RNI-Delete-Confirm-Button" class="button is-danger" onclick="_Pict.views.ReportNamedInstanceCreate.executeDelete()">
						<span class="icon"><i class="fas fa-trash"></i></span>
						<span>Delete</span>
					</button>
					<button class="button" onclick="_Pict.views.ReportNamedInstanceCreate.cancelDelete()">Cancel</button>
				</div>
			</footer>
		</div>
	</div>
</div>`
		},
		{
			Hash: 'RNI-JSONEditor-Container',
			Template: /*html*/`
<div class="rni-json-editor-inner">
	<div id="${'${ViewIdentifier}-CodeJar-Wrap'}" class="pict-code-editor-wrap"></div>
</div>`
		}
	],

	Renderables:
	[
		{
			RenderableHash: 'RNI-Create-Container',
			TemplateHash: 'RNI-Create-Container-Template',
			RenderMethod: 'replace'
		}
	]
});
